import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { studentNav } from "@/config/nav";
import { StatusBadge } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, Eye, Trophy, Download } from "lucide-react";
import { toast } from "sonner";

export default function StudentResults() {
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/student/attempts").then(({ data }) => { setAttempts(data); setLoading(false); });
  }, []);

  const download = async (id, title) => {
    try {
      const res = await api.get(`/attempts/${id}/report`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${title}-result.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Report available after results are published");
    }
  };

  return (
    <DashboardLayout nav={studentNav} title="Results">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Examination History</h1>
        <p className="text-muted-foreground mt-1">All your attempts and published results.</p>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
      ) : attempts.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <Trophy className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">No attempts yet. Take an exam to see results here.</p>
        </div>
      ) : (
        <div className="bg-card border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Examination</TableHead>
                <TableHead>Subject</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Score</TableHead>
                <TableHead className="text-right">%</TableHead>
                <TableHead>Result</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attempts.map((a) => (
                <TableRow key={a.id} data-testid={`attempt-row-${a.id}`}>
                  <TableCell className="font-medium">{a.exam_title}</TableCell>
                  <TableCell>{a.subject}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell className="text-right font-mono">{a.status === "published" ? `${a.total_score}/${a.total_marks}` : "—"}</TableCell>
                  <TableCell className="text-right font-mono">{a.status === "published" ? `${a.percentage}%` : "—"}</TableCell>
                  <TableCell>{a.status === "published" ? (a.passed ? <span className="text-emerald-600 font-medium">PASS</span> : <span className="text-destructive font-medium">FAIL</span>) : "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {a.status === "published" && (
                        <>
                          <Link to={`/app/results/${a.id}`}><Button size="icon" variant="ghost" data-testid={`view-${a.id}`}><Eye className="h-4 w-4" /></Button></Link>
                          <Button size="icon" variant="ghost" onClick={() => download(a.id, a.exam_title)} data-testid={`download-${a.id}`}><Download className="h-4 w-4" /></Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </DashboardLayout>
  );
}
